/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormLabel } from "@material-ui/core";
import { cloneDeep } from "lodash";

import checkCondition from "../../../lib/utils/checkCondition";
import checkValidation from "../../../lib/utils/checkValidation";
import AutoFormLayout from "../layout/AutoFormLayout";
import AutoFormGroupLayout from "../layout/AutoFormGroupLayout";
import AutoFormItem from "./AutoFormItem";
import AutoFormItemLayout from "../layout/AutoFormItemLayout";
import { getOption } from "../../../lib/api/getOptions";
import uiConf from "../../../config/uiConf";

const checkIsEmptyValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return true;
    }

    return false;
}

// 스키마는 서버가 한 번 만들어 두므로 「오늘」 기본값은 "today" 로 받아 창을 열 때 브라우저 날짜로 계산한다(날짜 칸에서 고른 날처럼 자정)
const resolveDefaultValue = (value) => {
    if (value !== "today") {
        return value;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today;
}

/**
 * @COMPONENT_NAME AutoForm
 * @DESCRIPTION
 *   - 스키마 기반의 동적 폼 생성 컴포넌트입니다.
 *   - schema.columns 정의를 바탕으로 입력 필드를 자동 생성하며, 그룹/순서/조건 등에 따라 UI가 동적으로 구성됩니다.
 *   - 파일 업로드(`formItemType: "file"`) 필드의 경우 logHistory 설정 시 파일 메타데이터를 자동 수집하여 `_activeFilesMeta`, `_deletedFilesMeta`에 포함시킵니다.
 *   - 생성(create)/수정(edit) 모드에 따라 초기값 처리, 비밀번호 필드 초기화, 숨김/필수/비활성 조건이 다르게 적용됩니다.
 *
 * @PROPS
 *   - {string} id: 각 입력 항목에 고유한 ID 접두어로 사용됨
 *   - {Object} schema: 폼 스키마 정보 (columns, formGroups, formOrder 등 포함)
 *   - {string} mode: "create" | "edit" 
 *   - {Object} initialData: 초기 입력값 객체
 *   - {boolean} noAutoFocus: true일 경우 첫 입력 필드에 autoFocus 적용 안함
 *   - {Function} onChange: formData가 변경될 때 호출되는 콜백
 *   - {Function} setInitialDataCallback: 최초 formData 생성 직후 커스터마이징을 위한 콜백
 *   - {Function} modifyFormDataCallback: 상위 컴포넌트로 전달받아 formData 변경
 *   - {Function} checkShowCallback: 필드 표시 여부 결정 함수 (formData, column) => boolean
 *   - {Function} checkDisabledCallback: 필드 비활성화 여부 결정 함수 (formData, column) => boolean
 *   - {Function} checkValidationCallback: 필드 유효성 재정의 함수 (formData, column) => [boolean, string]
 *   - {Function} barcodeCallback: 바코드 입력에 대한 사용자 정의 처리 함수
 *   - {Object} focusRefs: 각 필드별 ref를 모아 전달받는 React ref 객체
 *
 * @PASSTHROUGH_PROPS
 *   - {Object} others: 기타 props들은 AutoFormLayout 및 하위 레이아웃 컴포넌트로 전달됨
 *
 * @CALLBACKS
 *   - onChange(formData): 모든 필드의 상태가 변경될 때 호출됨
 *   - setInitialDataCallback(formData): formData 초기화 후 수동 조작 가능
 *   - modifyFormDataCallback(formData, name, value): formData 변경
 *   - checkShowCallback(formData, column): 필드 렌더 여부 반환
 *   - checkDisabledCallback(formData, column): 필드 disabled 상태 여부 반환
 *   - checkValidationCallback(formData, column): 사용자 정의 유효성 검사 반환
 *   - barcodeCallback(value, name): 바코드 전용 입력 처리 시 사용
 */
function AutoForm ( props ) {

    const { t } = useTranslation ();
    const { id, schema, mode, initialData, noAutoFocus, onChange, setInitialDataCallback, modifyFormDataCallback, checkShowCallback, checkDisabledCallback, checkValidationCallback, barcodeCallback, focusRefs, onAddressSelectCallback, ...others } = props;
    const [ formData, setFormData ] = useState( initialData );
    const [ orderedColumns, setOrderedColumns ] = useState([]);
    const [ interaction, setInteraction ] = useState( false );
    const [logHistory, setLogHistory] = useState(false);
    // edit 모드에서 부모가 넘긴 최신 initialData 와, 내부 formData 가 마지막으로 맞춘 initialData
    const latestInitialData = useRef( initialData );
    const syncedInitialData = useRef( initialData );
    latestInitialData.current = initialData;

    useEffect(() => {
		let mounted = true;

		(async () => {
			let logHistoryBoolean = await getOption("fileUpload", "logHistory");
			if (mounted) {
				if (logHistoryBoolean?.toLowerCase() === "true") {
					setLogHistory(true);
				} else {
					setLogHistory(false);
				}
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

    useEffect ( () => {

        setFormData ( ( prevFormData ) => {

            if ( schema ) {

                let newFormData = prevFormData ? cloneDeep( prevFormData ) : {};

                for ( const column of schema.columns ) {

                    if ( mode === "create" ) {

                        if ( column.formItemType === "file" ) {

                            newFormData[column.name] = {

                                deletedFiles: [],
                                activeFiles: []

                            }

                        } else {

                            newFormData[column.name] = resolveDefaultValue ( column.defaultValue );

                        }

                    } else {
    
                        if ( column.password ) {

                            newFormData[column.name] = "";

                        }

                    }

                }

                // modify form data with setInitialDataCallback if exist

                if ( setInitialDataCallback ) {

                    setInitialDataCallback ( newFormData );

                }

                return newFormData;
            
            } else {

                return {};

            }

        } );

        let orderedColumns = schema ? schema.columns.slice() : [];

        if ( schema ) {

            if ( schema.useFormOrder ) {

                if ( schema.formGroups && schema.formGroups.length > 0 ) {

                    orderedColumns.sort( ( columnA, columnB ) => ( ( columnA.formGroup ? columnA.formGroup : 0 ) - ( columnB.formGroup ? columnB.formGroup : 0 ) ) ? ( ( columnA.formGroup ? columnA.formGroup : 0 ) - ( columnB.formGroup ? columnB.formGroup : 0 ) ) : ( ( columnA.formOrder ? columnA.formOrder : 0 ) - ( columnB.formOrder ? columnB.formOrder : 0 ) )) ;

                } else {
  
                    orderedColumns.sort( ( columnA, columnB ) => ( columnA.formOrder ? columnA.formOrder : 0 ) - ( columnB.formOrder ? columnB.formOrder : 0 ) );

                }

            }
            else {

                if ( schema.formGroups && schema.formGroups.length > 0 ) {

                    orderedColumns.sort( ( columnA, columnB ) => ( columnA.formGroup - columnB.formGroup ) );

                }
            }

        }

        setOrderedColumns ( orderedColumns );
    
    }, [schema] );

    useEffect ( () => {

        if ( mode === "edit" && schema ) {

            // transform file type form data here

            let formData = initialData ? cloneDeep(initialData) : {};

            for ( const column of schema.columns ) {

                if ( column.formItemType === "file" ) {

                    if ( column.maxFiles === undefined || column.maxFiles > 1 ) {

                        formData[column.name] = {
    
                            deletedFiles: [],
                            activeFiles: ( formData[column.name] && formData[column.name].length > 0 ) ? JSON.parse ( formData[column.name] ).map ( ( url ) => ( {
                                type: "url",
                                url: url
                            } ) ) : []
                            
                        }
    
                    } else {
    
                        formData[column.name] = {
    
                            deletedFiles: [],
                            activeFiles:
                                ( formData[column.name] && formData[column.name].length > 0 )
                                ?
                                [
                                    {
                                        type: "url",
                                        url: formData[column.name]
                                    }
                                ]
                                :
                                []
    
                        }
                    }
                }
    
            }

            syncedInitialData.current = initialData;

            setFormData ( formData );

        }
        
    }, [initialData] );

    const applyChange = ( baseFormData, name, value ) => {

            let newFormData = { ...baseFormData, [name]: value };
             const column = schema?.columnMap?.[name];
             
             // 파일 타입이면 metadata 자동 추가
            if ( column && column?.formItemType === "file" && logHistory) {
       
                newFormData["_activeFilesMeta"] = newFormData?.["_activeFilesMeta"] || {};
                newFormData["_deletedFilesMeta"] = newFormData?.["_deletedFilesMeta"] || {};

                newFormData["_activeFilesMeta"][name] = newFormData[name].activeFiles.map((file) => {
                    if (file.type === "url") return file;
                    return {
                        meta: {
                            description: file.description || "",
                            operation: file.operation || "add",
                            fileName: file.file?.name || file.name || "",
                            tableName: schema.name,
                            columnName: column.name,
                        },
                    };
                });

                newFormData["_deletedFilesMeta"][name] = Array.isArray(newFormData[name]?.deletedFilesMeta)
                ? newFormData[name].deletedFilesMeta.map((file) => ({
                    meta: {
                        description: file.description || "",
                        operation: file.operation || "delete",
                        fileName: decodeURIComponent(file.url),
                        tableName: schema.name,
                        columnName: column.name,
                    },
                }))
                : [];
            }
           
            if ( modifyFormDataCallback ) {

                modifyFormDataCallback ( newFormData, name, value );

            }

            return newFormData;

    };

    const onChangeFormItem = ( name, value ) => {
       
        setFormData ( ( prevFormData ) => {
            
            let newFormData = applyChange ( prevFormData, name, value );

            const baseInitialData = syncedInitialData.current;
            const edited = value !== prevFormData?.[name] && ! ( checkIsEmptyValue ( value ) && checkIsEmptyValue ( prevFormData?.[name] ) );

            setTimeout ( () => {

                // 그 사이 부모가 새 initialData 를 넘겼으면 newFormData 는 옛 값 바탕이다 — 통째로 보내면 부모 값을 덮는다.
                // 칸이 처음 보내는 빈 값 · 받은 값 되돌림은 버리고, 사용자가 바꾼 값만 새 initialData 위에 얹어 보낸다
                if ( mode === "edit" && latestInitialData.current !== baseInitialData ) {

                    if ( edited ) {

                        onChange ( applyChange ( latestInitialData.current, name, value ) );

                    }

                    return;

                }

                onChange ( newFormData );

            }, 0 ); // to improve latency

            return newFormData;
            
        } );
        setInteraction ( true );

    };

    let tabIndex = 0;

    return (

        <AutoFormLayout {...others} >
        {
            ( ( schema && schema.formGroups && schema.formGroups.length > 0 ) ? schema.formGroups : [""] )
                .map ( ( formGroupName, index ) => {
                    return (

                        <div key={index}>
                        
                        { index === 0 ? <></> : ( uiConf.displayRulerBetweenFormGroup ? <hr></hr> : <></> ) } {/* Horizontal Ruler between Form Groups */}
                    { ( schema?.useFormGroupName && formGroupName !== "" ) ? <FormLabel component="legend">{ t ( formGroupName ) }</FormLabel> : <></> } {/* FormGroup Name */}
                        
                        <AutoFormGroupLayout  {...others} >
                        
                        {   
                            orderedColumns
                                .filter ( ( column ) =>  {
                                    
                                    if ( column.hideInForm || ( mode === "create" && column.hideOnCreate ) || ( mode === "edit" && column.hideOnEdit ) ) {

                                        return false;

                                    }

                                    if ( column.requiredOn ) {

                                        column.required = checkCondition ( formData, column.requiredOn );
                                        
                                    }

                                    if ( column.hideOn ) {

                                        let hideByCondition = checkCondition ( formData, column.hideOn );
 
                                        if ( hideByCondition ) {

                                            return false;

                                        }

                                    }

                                    if ( column.formGroup === undefined) {

                                        return true;

                                    }

                                    if ( column.formGroup !== index ) {
                                       
                                        return false;
                                    }

                                    return true;

                                } )
                                .filter ( ( column ) => ( checkShowCallback ? checkShowCallback ( formData, column ) : true ) )
                                .map ( ( column ) => {

                                    let readonly = column.readonly || column.key === "PRI";

                                    let disabled = column.disabled;

                                    if ( ! disabled && column.disableOn ) {

                                        let disabledByCondition = checkCondition ( formData, column.disableOn );

                                        if ( disabledByCondition ) {

                                            disabled = true;
                                        }

                                    }

                                    if ( ! disabled && checkDisabledCallback ) {

                                        disabled = checkDisabledCallback ( formData, column );

                                    }

                                    let [ valid, helperText ] = checkValidation ( mode, column, column.validation, checkIsEmptyValue(formData[column.name]) ?  "" : formData[column.name]);

                                    // ignore password legth validation when edit mode

                                    if ( mode === "edit" && column.password && ( formData[column.name] || "" ) === "" ) {

                                        [ valid, helperText ] = [ true, "" ];

                                    }

                                    if ( valid && checkValidationCallback ) {

                                        [ valid, helperText ] = checkValidationCallback ( formData, column );

                                    }

                                    if ( ! ( disabled || ( mode === "edit" && readonly ) ) ) {

                                        tabIndex++;

                                    }

                                    return (

                                        <AutoFormItemLayout key={column.name} column={column}>

                                            <AutoFormItem

                                                id = { id + "-" + column.name }
                                                label = { column.label ? column.label : t ( `columnName.${column.displayName || column.name}` ) }
                                                name = { column.name }

                                                disabled = { disabled || ( mode === "edit" && readonly ) }

                                                error = { interaction && ! valid }
                                                helperText = { interaction && helperText }

                                                column = { column }
                                                value = { formData && ( checkIsEmptyValue(formData[column.name]) ? ( checkIsEmptyValue(column.defaultValue) ? "" : resolveDefaultValue(column.defaultValue) ) : formData[column.name] ) }

                                                onChange = { ( mode === "edit" && readonly )  ? () => {} : ( value ) => onChangeFormItem ( column.name, value ) }
                                                
                                                autoFocus = { ( tabIndex === 1 ) && ( ! noAutoFocus ) }

                                                barcodeCallback = {barcodeCallback}
                                                onAddressSelectCallback = {onAddressSelectCallback}
                                                focusRef = {(el)=> {
                                                    if (typeof focusRefs !== 'undefined') {
                                                        focusRefs.current[column.name]=el
                                                    }
                                                }}
                                                focusRefCurrent = {focusRefs?.current[column.name]}
                                                formData = {formData}
                                            />

                                        </AutoFormItemLayout>

                                    );

                                } )

                        }

                        </AutoFormGroupLayout>

                        </div>

                    );

                } )
        }
        </AutoFormLayout>

    )

}

export default AutoForm;

